import { DataSource } from "@angular/cdk/collections";
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormControl } from "@angular/forms";
import {
    MatDatepicker,
    MatPaginator,
    MatSelectChange,
    MatSort,
} from "@angular/material";
import * as moment from "moment";
import { Observable } from "rxjs/Observable";
import { fuseAnimations } from "../../../../core/animations";
import { ORDER_STATUS } from "../../../../core/services/orders/orders.constant";
import { OrderAdminListQuery } from "../../../../core/services/orders/order.models";
import { UsersService } from "../../../../core/services/users/users.service";
import { VoutchersService } from "../../../../core/services/vouchers/vouchers.service";
import { CategoriesService } from "../categories/categories.service";
import { DashboardOrdersService } from "./orders.service";

@Component({
    selector: "fuse-orders",
    templateUrl: "./orders.component.html",
    styleUrls: ["./orders.component.scss"],
    animations: fuseAnimations,
})
export class OrdersComponent implements OnInit, AfterViewInit {
    operator: string;

    statuses = [
        ORDER_STATUS.PAYMENT_ACCEPTED,
        ORDER_STATUS.RESERVED,
        ORDER_STATUS.AWAITING_PAYMENT,
        ORDER_STATUS.CANCELED,
        ORDER_STATUS.REFUNDED,
        // ORDER_STATUS.PAYMENT_ABANDONED,
        // ORDER_STATUS.PAYMENT_ERROR,
        // ORDER_STATUS.PAYMENT_EXPIRED,
        ORDER_STATUS.PAYMENT_REFUSED,
        ORDER_STATUS.PAID,
        ORDER_STATUS.ORDER_CANCELEDBYUSER,
    ];

    dataSource: FilesDataSource | null;
    displayedColumns = [
        "boarded",
        "invoiceNum",
        "type" /*, 'direction'*/,
        "user",
        "phone",
        "quantity",
        "amount",
        "items",
    ];

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild("filter") filter: ElementRef;
    @ViewChild("startDatePicker") startDatePicker: MatDatepicker<any>;
    @ViewChild("endDatePicker") endDatePicker: MatDatepicker<any>;
    @ViewChild("from") fromDate: ElementRef;
    @ViewChild("to") toDate: ElementRef;
    @ViewChild(MatSort) sort: MatSort;

    selectedDates: any;
    inputFrom: FormControl;
    inputTo: FormControl;

    constructor(
        public readonly categoriesService: CategoriesService,
        readonly ordersService: DashboardOrdersService,
        readonly usersService: UsersService,
        public readonly vouchersService: VoutchersService
    ) {
        this.selectedDates = {
            from: moment().startOf("month"),
            to: moment().endOf("month"),
        };
        this.inputFrom = new FormControl(this.from);
        this.inputTo = new FormControl(this.to);
    }

    set status(status: string) {
        localStorage.setItem("order_status", status);
    }
    get status() {
        return localStorage.getItem("order_status");
    }

    set category(category: string) {
        localStorage.setItem("order_category", category);
    }
    get category() {
        return localStorage.getItem("order_category");
    }

    //#region Dates
    toUTCSmallDate(date?: any) {
        if (!date || typeof date === "undefined") {
            date = new Date();
        }
        if (date instanceof moment) {
            date = (date as moment.Moment).toDate();
        }
        if (typeof date === "string") {
            date = new Date(date);
        }
        return new Date(
            Date.UTC(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                0,
                0,
                0
            )
        );
    }

    get from() {
        const val = localStorage.getItem("order_from");
        if (val) {
            return this.toUTCSmallDate(val);
        }
        return this.toUTCSmallDate(this.selectedDates.from);
    }

    set from(from: Date) {
        if (!from) {
            localStorage.removeItem("order_from");
            return;
        }
        localStorage.setItem("order_from", from.toISOString());
    }

    set to(to: Date) {
        if (!to) {
            localStorage.removeItem("order_to");
            return;
        }
        localStorage.setItem("order_to", to.toISOString());
    }

    get to() {
        const val = localStorage.getItem("order_to");
        if (val) {
            return this.toUTCSmallDate(val);
        }
        return this.toUTCSmallDate(this.selectedDates.to);
    }

    changeFrom(event: any) {
        if (event) {
            const date = this.toUTCSmallDate(event);
            this.from = date;
            this.refresh();
        }
    }

    changeTo(event: any) {
        if (event) {
            const date = this.toUTCSmallDate(event);
            this.to = date;
            this.refresh();
        }
    }
    //#endregion Dates

    ngOnInit() {
        if (!this.status) {
            this.status = ORDER_STATUS.PAYMENT_ACCEPTED;
        }

        this.ordersService.page = this.getStoredNumber("ordersPageNumber", 0);
        this.ordersService.limit = this.getStoredNumber("ordersPageLength", 10);
        this.dataSource = new FilesDataSource(this.ordersService);

        this.usersService.list();
        this.categoriesService.getCategories();
    }

    ngAfterViewInit() {
        Observable.fromEvent(this.filter.nativeElement, "keyup")
            .debounceTime(150)
            .distinctUntilChanged()
            .subscribe(() => {
                this.paginator.pageIndex = 0;
                this.storePaginatorState(0, this.paginator.pageSize);
                this.loadOrders({
                    page: 0,
                    limit: this.paginator.pageSize,
                    search: this.getCurrentSearch(),
                });
            });

        this.paginator.page.subscribe(() => {
            this.storePaginatorState(
                this.paginator.pageIndex,
                this.paginator.pageSize
            );
            this.loadOrders({
                page: this.paginator.pageIndex,
                limit: this.paginator.pageSize,
            });
        });

        this.sort.sortChange.subscribe(() => {
            this.paginator.pageIndex = 0;
            this.storePaginatorState(0, this.paginator.pageSize);
            this.loadOrders({
                page: 0,
                limit: this.paginator.pageSize,
                sortBy: this.sort.direction ? this.sort.active : "",
                sortDirection: this.sort.direction || "",
            });
        });

        this.loadOrders();
    }

    storePaginatorState(page: number = this.ordersService.page, limit: number = this.ordersService.limit) {
        localStorage.setItem(
            "ordersPageNumber",
            page.toString()
        );
        localStorage.setItem(
            "ordersPageLength",
            limit.toString()
        );
    }

    refresh() {
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }
        this.storePaginatorState(0, this.ordersService.limit);
        this.loadOrders({
            page: 0,
            limit: this.ordersService.limit,
        });
    }

    reset() {
        this.operator = null;
        this.category = "";
        this.status = ORDER_STATUS.PAYMENT_ACCEPTED;
        this.startDatePicker._select(null);
        this.endDatePicker._select(null);
        this.fromDate.nativeElement.value = null;
        this.toDate.nativeElement.value = null;
        this.from = null;
        this.to = null;
        this.refresh();
    }

    selectChange(event: MatSelectChange) {
        this.refresh();
    }

    operatorChange(event: MatSelectChange) {
        this.refresh();
    }

    hasBoardedItems(order) {
        return order.items.some((item) => item.boarded > 0);
    }

    private getStoredNumber(key: string, fallback: number) {
        const value = Number.parseInt(localStorage.getItem(key), 10);
        return Number.isNaN(value) ? fallback : value;
    }

    private getCurrentSearch() {
        return this.filter && this.filter.nativeElement
            ? this.filter.nativeElement.value || ""
            : "";
    }

    private buildQuery(overrides: OrderAdminListQuery = {}): OrderAdminListQuery {
        return {
            status: this.status,
            from: this.from,
            to: this.to,
            operator: this.operator,
            category: this.category,
            page: this.ordersService.page,
            limit: this.ordersService.limit,
            search: this.getCurrentSearch(),
            sortBy:
                this.sort && this.sort.direction
                    ? this.sort.active
                    : this.ordersService.sortBy || "",
            sortDirection:
                this.sort && this.sort.direction
                    ? this.sort.direction
                    : this.ordersService.sortDirection || "",
            ...overrides,
        };
    }

    private loadOrders(overrides: OrderAdminListQuery = {}) {
        return this.ordersService.loadOrders(this.buildQuery(overrides));
    }
}

export class FilesDataSource extends DataSource<any> {
    constructor(private ordersService: DashboardOrdersService) {
        super();
    }

    /** Connect function called by the table to retrieve one stream containing the data to render. */
    connect(): Observable<any[]> {
        return this.ordersService.onOrdersChanged;
    }

    disconnect() {}
}



// WEBPACK FOOTER //
// /usr/app/src/app/main/content/app/orders/orders.component.ts